import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabaseConfigured } from '../shared/config';
import { Banner, Button, Field, Input, Spinner } from '../components/ui';
import { api } from '../lib/api';
import { extensionRequest, sendTokenToExtension, type BridgeResult } from '../lib/extensionBridge';
import { useSession } from '../lib/session';
import { currentAccessToken, sendMagicLink, signInWithPassword, verifyEmailCode } from '../lib/supabase';

/**
 * Magic link first, password as the fallback for anyone whose inbox is a
 * problem on a shared office machine. When the Chrome extension sent the user
 * here it also passed its own id, and a successful sign-in hands the token back.
 */
export default function SignIn() {
  const { contractor, loading, refresh, error: accountError } = useSession();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'link' | 'password'>('link');
  const [status, setStatus] = useState<'idle' | 'working' | 'sent'>('idle');
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [bridge, setBridge] = useState<BridgeResult | null>(null);
  const [roleSwitch, setRoleSwitch] = useState<'idle' | 'working' | 'done' | 'refused'>('idle');
  const [roleError, setRoleError] = useState<string | null>(null);

  const { wanted: forExtension } = extensionRequest(location.search);
  const params = new URLSearchParams(location.search);
  const invite = params.get('invite');
  const company = params.get('company');
  const role = params.get('role') === 'homeowner' ? 'homeowner' : 'contractor';
  // Run 9: /sign-in?kind=brokerage — a brokerage signs up through the same
  // door; no wizard, the flag just flips the office kind.
  const brokerage = params.get('kind') === 'brokerage';

  useEffect(() => {
    if (invite && !email) setEmail(invite);
  }, [invite, email]);

  // Supabase allows one sign-in email per address per 60s; mirror it so Resend
  // never produces a rate-limit error.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  // Flip a brand-new office to brokerage exactly once — never an office that
  // already ran (or skipped) first-run setup, so nobody's contractor company
  // turns into a brokerage by clicking the wrong link.
  useEffect(() => {
    if (!brokerage || !contractor) return;
    if (contractor.companyKind === 'brokerage' || contractor.officeSetupCompletedAt != null) return;
    void api.put('/api/me', { companyKind: 'brokerage' }).then(() => refresh());
  }, [brokerage, contractor, refresh]);

  // The homeowner door (/sign-in?role=homeowner) makes the account a homeowner
  // account. The server only allows it on an account with no activity yet, so
  // a contractor with jobs who follows the wrong link keeps their office.
  const needsHomeowner = role === 'homeowner' && !!contractor && contractor.accountRole !== 'homeowner';
  useEffect(() => {
    if (!needsHomeowner || roleSwitch !== 'idle') return;
    setRoleSwitch('working');
    api
      .put('/api/me', { accountRole: 'homeowner' })
      .then(() => refresh())
      .then(() => setRoleSwitch('done'))
      .catch((err: unknown) => {
        setRoleError(err instanceof Error ? err.message : 'This account could not be set up as a homeowner account.');
        setRoleSwitch('refused');
      });
  }, [needsHomeowner, roleSwitch, refresh]);

  // Hand the token to the extension as soon as a session exists — this also
  // covers the magic link landing back in this tab.
  useEffect(() => {
    if (!forExtension || !contractor) return;
    let cancelled = false;
    void (async () => {
      const token = await currentAccessToken();
      if (!token || cancelled) return;
      setBridge(await sendTokenToExtension(location.search, token));
    })();
    return () => {
      cancelled = true;
    };
  }, [forExtension, contractor, location.search]);

  if (!loading && contractor && !forExtension && needsHomeowner) {
    if (roleSwitch === 'idle' || roleSwitch === 'working') return <Spinner label="Setting up your homeowner account…" />;
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg px-6">
        <div className="w-full max-w-sm space-y-4">
          <Banner tone="watch" title="This email already has a contractor account">
            {roleError ?? 'This account could not be set up as a homeowner account.'}
          </Banner>
          <a href="/dashboard" className="block text-center text-sm font-semibold text-tint">
            Go to the contractor dashboard
          </a>
        </div>
      </div>
    );
  }

  if (!loading && contractor && !forExtension) {
    const requested = (location.state as { from?: string } | null)?.from ?? params.get('returnTo');
    const from = requested?.startsWith('/') && !requested.startsWith('//') && !requested.includes('\\') ? requested : null;
    const home = contractor.accountRole === 'homeowner' ? '/properties' : '/dashboard';
    return <Navigate to={from && from !== '/sign-in' ? from : home} replace />;
  }

  const sendCode = async () => {
    await sendMagicLink(email.trim());
    setCode('');
    setCooldown(60);
    setStatus('sent');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const codeStep = mode === 'link' && status === 'sent';
    setStatus('working');
    try {
      if (codeStep) {
        await verifyEmailCode(email.trim(), code);
        await refresh();
        setStatus('idle');
      } else if (mode === 'link') {
        await sendCode();
      } else {
        await signInWithPassword(email.trim(), password);
        await refresh();
        setStatus('idle');
      }
    } catch (err) {
      setError(friendlyAuthError(err));
      setStatus(codeStep ? 'sent' : 'idle');
    }
  };

  const resend = async () => {
    setError(null);
    try {
      await sendCode();
    } catch (err) {
      setError(friendlyAuthError(err));
    }
  };

  const codeStep = mode === 'link' && status === 'sent';

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <p className="text-2xl font-extrabold tracking-tight text-ink">
            Lien<span className="text-tint">Guardian</span>
          </p>
          <p className="mt-1 text-sm text-muted">
            {role === 'homeowner'
              ? 'Add your properties. Watch for mechanic’s liens.'
              : brokerage
                ? 'Your brokerage’s office — roster your listing agents.'
                : 'Florida construction lien compliance'}
          </p>
        </div>

        {invite && (
          <div className="mb-4">
            <Banner tone="ok" title={company ? `${company} invited you` : 'You have been invited'}>
              Sign in with {invite} to join the team.
            </Banner>
          </div>
        )}

        {forExtension && contractor && (
          <div className="mb-4">
            {bridge === 'sent' ? (
              <Banner tone="ok" title="Extension connected ✓">You can close this tab.</Banner>
            ) : bridge === null ? (
              <Banner tone="watch" title="Connecting the extension…">Keep this tab open for a moment.</Banner>
            ) : (
              <Banner tone="urgent" title="Could not reach the extension">
                Open the LienGuardian extension popup and try “Sign in” again from there.
              </Banner>
            )}
          </div>
        )}

        {!supabaseConfigured && (
          <div className="mb-4">
            <Banner tone="urgent" title="Supabase is not configured">
              Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY, then reload.
            </Banner>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4 rounded-xl border border-border bg-surface p-6">
          <Field label="Work email">
            <Input
              type="email"
              required
              autoComplete="email"
              readOnly={codeStep}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </Field>

          {mode === 'password' && (
            <Field label="Password">
              <Input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          )}

          {codeStep && (
            <>
              <Banner tone="ok" title="Check your inbox">
                We sent a sign-in code to {email}. Type it below — or tap the link in the email if you are on this device.
              </Banner>
              <Field label="Sign-in code" hint="The code from the email. It expires after an hour.">
                <Input
                  type="text"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={10}
                  placeholder="12345678"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </Field>
            </>
          )}

          {error && <Banner tone="urgent">{error}</Banner>}
          {!error && accountError && !contractor && (
            <Banner tone="urgent" title="Signed in, but your account could not open">
              {accountError}
            </Banner>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={status === 'working' || !supabaseConfigured || (codeStep && code.length < 6)}
          >
            {status === 'working'
              ? 'Working…'
              : codeStep
                ? 'Sign in'
                : mode === 'link'
                  ? 'Email me a sign-in code'
                  : 'Sign in'}
          </Button>

          {codeStep && (
            <div className="flex justify-between text-xs font-semibold text-muted">
              <button type="button" onClick={() => void resend()} disabled={cooldown > 0} className="hover:text-ink disabled:opacity-50">
                {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStatus('idle');
                  setCode('');
                  setError(null);
                }}
                className="hover:text-ink"
              >
                Use a different email
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setMode(mode === 'link' ? 'password' : 'link');
              setStatus('idle');
              setError(null);
            }}
            className="w-full text-center text-xs font-semibold text-muted hover:text-ink"
          >
            {mode === 'link' ? 'Use a password instead' : 'Email me a code instead'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-dim">
          {role === 'homeowner'
            ? 'Homeowners track properties here. Contractor tools stay on the other door.'
            : 'Field crews use the LienGuardian mobile app. This portal is for office staff.'}
        </p>
      </div>
    </div>
  );
}

function friendlyAuthError(err: unknown): string {
  const msg = err instanceof Error ? err.message : 'Sign-in failed';
  const m = msg.toLowerCase();
  if (m.includes('expired') || m.includes('invalid')) return 'That code is wrong or has expired. Use the newest email, or resend a code.';
  if (m.includes('rate') || m.includes('security purposes')) return 'Please wait a minute before requesting another code.';
  return msg;
}
